import './frozenlake.css';

import ListGroup from 'react-bootstrap/ListGroup';
import Table from 'react-bootstrap/Table';

import { CopyBlock, dracula } from "react-code-blocks";

import Markdown from 'react-markdown';
import remarkMath from 'remark-math';
import { MathJaxContext, MathJax } from 'better-react-mathjax';
import Alert from 'react-bootstrap/Alert';

import eightByEightPreview from './Files/Resources/8x8preview.gif'
import observeAction from './Files/Resources/ObserveAction.png'
import fourbyfour from './Files/Resources/4x4Image.png'
import eightbyeight from './Files/Resources/8x8Image.png'
import custommap from './Files/Resources/custommap4x4.png'
import largemap from './Files/Resources/largemap.png'
import plotspng from './Files/Resources/plots.png'
import toofewepisodes from './Files/Resources/TooFewEpisodes.png'
import toomanyepisodes from './Files/Resources/TooManyEpisodes.png'
import idealepisodes from './Files/Resources/IdealEpisodes.png'
import optimalpath1 from './Files/Resources/OptimalPath1.gif'
import slip40k from './Files/Resources/slip40k.png'
import slip85k from './Files/Resources/slip85k.png'
import slip450k from './Files/Resources/slip450k.png'
import slipvisual from './Files/Resources/SlipVisual.gif'


function MyCodeComponent({ code, language }) {
  return (


    


    <CopyBlock
      text={code}
      language={language}
      showLineNumbers={true}
      theme={dracula}
      wrapLines
    />
  );
}
    

function FrozenLake(){

  const markdownContentOne = `
## Brief Gymnasium Overview
[Gymnasium](https://gymnasium.farama.org/) is a useful Python library based on [OpenAI's Gym library](https://github.com/openai/gym?tab=readme-ov-file). This library provides a variety of environments for training and testing reinforcement learning algorithms. You control an agent by choosing from different actions, influencing what state the agent ends up in next. Each environment in Gymnasium provides a non-zero reward (negative or positive) after each step and/or upon reaching the desired end goal. These rewards help reinforcement learning algorithms determine the effectiveness of actions taken in a given state, allowing adjustments in future episodes to determine the best possible action for each state. The environments offer various options that can increase the complexity of finding optimal solutions.

## The Frozen Lake Environment
For the purpose of testing Q-Learning in a relatively simple environment, I will be utilizing the [Frozen Lake](https://gymnasium.farama.org/environments/toy_text/frozen_lake/) environment. The goal of Frozen Lake is to cross a frozen lake from the starting point to the goal without falling into any holes. Frozen Lake comes in both a 4x4 and 8x8 environment, for this guide I will be using the 8x8 environment to make the challenge slightly more complex, and gather more insights in the process.
`;




  return (
      <div id="frozenlakediv">

      <div className="leftrightdiv">
          <h1>Frozen Lake Reinforcement Learning Step by Step Guide</h1>
          <a href="/#/projects" class="returntorpojects">Return to Projects</a>
      </div>

      <ListGroup variant="flush">
          <ListGroup.Item><b>Category:</b> Reinforcement Learning (Q-Learning)</ListGroup.Item>
          <ListGroup.Item><b>Language:</b> Python</ListGroup.Item>
          <ListGroup.Item><b>Link:</b> <a href="https://github.com/adadamc/FrozenLake_StepByStep" target="_blank">GitHub</a></ListGroup.Item>
          <ListGroup.Item><b>Notes:</b> Guide (via <a href="https://github.com/adadamc/FrozenLake_StepByStep/blob/main/README.md" target="_blank">README</a>) and code (via <a href="https://github.com/adadamc/FrozenLake_StepByStep/blob/main/RL_FrozenLake.ipynb" target="_blank">Jupyter Notebook</a>) also available via above GitHub link</ListGroup.Item>
      </ListGroup>

      <br></br>

      <Alert variant="warning">
      <Alert.Heading>The original version of this guide is on GitHub!</Alert.Heading>
      <p>
        The original guide has been adapted for this website. The original can be found here: <a id="speciallink" href="https://github.com/adadamc/FrozenLake_StepByStep" target="_blank">GitHub</a>.
      </p>
    </Alert>

      <br></br>

      <MathJaxContext> 
      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {markdownContentOne}
      </Markdown>

      <img src={eightByEightPreview} alt="Frozen Lake 8x8 Environment" />
      <br></br>
      <br></br>
      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Understanding and Traversing the Environment`}
      </Markdown>

      
      <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Space</th>
          <th>Size</th>
          <th>Values</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Action Space</td>
          <td>Discrete(4)</td>
          <td>0: Move left, 1: Move down, 2: Move right, 3: Move up</td>
        </tr>
        <tr>
          <td>Observation Space</td>
          <td>Discrete(64)</td>
          <td>0-63 (Position calculation: current_row * ncols + current_col)</td>
        </tr>
      </tbody>
    </Table>

    <img src={observeAction} alt="Observation and Action Space" />

    <br></br>
    <br></br>

    In the above image we can see that the observation space is represented as integers from 0-63 where 0 is the starting position (top left) and 63 is the goal position (bottom right). You can calculate a position as the <code>current_row * ncols + current_col</code>. As mentioned in the table, the agent can take 1 of 4 actions:

  <ul>
    <li><b>0:</b> Move left</li>
    <li><b>1:</b> Move down</li>
    <li><b>2:</b> Move right</li>
    <li><b>3:</b> Move up</li>
  </ul>

  In this example, taking action 1 (move down) moves the agent to position 17 (unless is_slippery is enabled, while is explained below).
<br></br>
<br></br>

  <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### is_slippery`}
      </Markdown>

      When creating the environment, <code>is_slippery</code> can be set to True or False.
      If disabled, you will always move in your intended direction. If enabled, you will only move in your intended direction 1/3 of the time, 
      otherwise, you have an equal 1/3 chance of slipping in either perpendicular direction. For example:

      <br></br>
      <br></br>

      <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Reached</th>
          <th>Reward</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Goal</td>
          <td>+1</td>
        </tr>
        <tr>
          <td>Hole</td>
          <td>0</td>
        </tr>
        <tr>
          <td>Frozen</td>
          <td>0</td>
        </tr>
      </tbody>
    </Table>

    <br></br>

    <b>Possible Endings:</b>
    <ul>
    <li>Goal Reached</li>
    <li>Player moves into a hole</li>
    <li>Length of episode (actions taken) reach 200 (100 if using 4x4 environment)</li>
  </ul>

  While there is technically no negative reward, reaching the episode limit or falling into a hole is undesirable as we will not get a reward for that episode, and we will be unable to tell if the actions we took in that episode were in the
   "right direction" up to a certain point as we receive no positive feedback for partial progress.
<br></br>
<br></br>
   <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Setup
### Libraries / Imports`}
      </Markdown>

      <b>We will need a few libraries in order to:</b>
    <ul>
    <li>Use Gymnasium</li>
    <li>Debug / Monitor Performance of our Q-Learning Algorithm</li>
  </ul>


  These can be imported as:
  <CopyBlock
          text={`import gymnasium as gym
import numpy as np
import time
import math
from matplotlib import pyplot as plt`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Initializing the Environment`}
      </Markdown>
      The Frozen Lake environment can be initialized with the <code>gym.make('FrozenLake-v1')</code> command. There are various arguments that 
      can be used to customize the environment.

      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### map_name`}
      </Markdown>

      The <code>map_name</code> argument allows us to specify the map size,
      either <code>4x4</code> or <code>8x8</code>. In our examples, we will be
      using <code>8x8</code>.

      <CopyBlock
          text={`gym.make('FrozenLake-v1', map_name="8x8")`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th><code>gym.make('FrozenLake-v1', map_name="8x8")</code></th>
          <th><code>gym.make('FrozenLake-v1', map_name="4x4")</code></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><img src={eightbyeight} alt="" /></td>
          <td><img src={fourbyfour} alt="" /></td>
        </tr>
      </tbody>
    </Table>

    <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### desc`}
      </Markdown>
      The <code>desc</code> argument can be used to customize the map.

      <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Letter</th>
          <th>Tile</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>S</td>
          <td>Start Tile</td>
        </tr>
        <tr>
          <td>G</td>
          <td>Goal Tile</td>
        </tr>
        <tr>
          <td>F</td>
          <td>Frozen Tile</td>
        </tr>
        <tr>
          <td>H</td>
          <td>Tile with a Hole</td>
        </tr>
      </tbody>
    </Table>

    The <code>desc</code> argument is written in the following format (for 4x4): <code>desc=["SFFF", "FHFH", "FFFH", "HFFG"]</code>.

    For example, <code>env = gym.make("FrozenLake-v1", map_name="4x4", render_mode="human", desc=["SHHH", "HHHH", "GGGG", "FFFF"])</code>
    will create the following environment:
    <br></br>
    <br></br>
    <img src={custommap} alt="" />
    <br></br>
    Additionally, the generate_random_map function can be imported and used to generate a random map of any size.

    <CopyBlock
          text={`from gymnasium.envs.toy_text.frozen_lake import generate_random_map
env = gym.make("FrozenLake-v1", desc=generate_random_map(size=12), render_mode="human")`}
          language="python"
          theme={dracula}
          wrapLines
        />

        <br></br>

        The above code generated this 12x12 environment:
        <br></br>
        <img src={largemap} alt="" />
        <br></br>
        <br></br>
        <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### render_mode`}
      </Markdown>

      To help visualize what is going on the environment, there are various render options. By default, <code>render_mode</code> is set to None as it
       would not make sense to visually print tens of thousands, or even millions of episodes.

       <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>render_mode</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>None</td>
          <td>No render is computed <b>(Default)</b></td>
        </tr>
        <tr>
          <td>human</td>
          <td>Continuous rendering in the current display / terminal. The images shown earlier in this guide are all from the human render_mode.</td>
        </tr>
        <tr>
          <td>rgb_array</td>
          <td>Not used in this guide, more info: <a href="https://gymnasium.farama.org/api/env/#gymnasium.Env.render" target="_blank">here</a></td>
        </tr>
        <tr>
          <td>ansi</td>
          <td>Not used in this guide, more info: <a href="https://gymnasium.farama.org/api/env/#gymnasium.Env.render" target="_blank">here</a></td>
        </tr>
        <tr>
          <td>rgb_array_list</td>
          <td>Not used in this guide, more info: <a href="https://gymnasium.farama.org/api/env/#gymnasium.Env.render" target="_blank">here</a></td>
        </tr>
      </tbody>
    </Table>

    <b></b>
    <b>For now, we will create a function <code>run_episodes</code> to allow us to run a specified number of episodes in an environment,
    gather the results, and specify variables in our Q-Learning algorithm.</b>

    <CopyBlock
          text={`def run_episodes(episodes, learning_rate=0.05, discount_factor=0.95, epsilon=1, epsilon_change=0.01, slippery=True, render=None):
    env = gym.make("FrozenLake-v1", map_name="8x8", is_slippery=slippery, render_mode=render)`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
I have set some default values for a lot of these parameters, however,
 optimal values for these can vary based on map size, episode count, the `is_slippery` setting,
  and any other environment changes. `learning_rate`, `discount_factor`, `epsilon`, and `epsilon_change` will
   be explained further down in the guide (as it relates more to the Q-Learning calculation that will be used).
    The other parameters do the following:

    <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>episodes</td>
          <td>Episode count (ex. 500 episodes would mean that the "game" is played 500 times until we reach one of the end conditions for each episode)</td>
        </tr>
        <tr>
          <td>slippery</td>
          <td><code>True</code> or <code>False</code>, enables or disables <code>is_slippery</code> option in the environment</td>
        </tr>
        <tr>
          <td>render</td>
          <td>One of the above render_mode options can be specified here. <code>'human'</code> is useful, however, it should not be used for a large episode count as it significantly increases runtime</td>
        </tr>
      </tbody>
    </Table>

    <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Keeping Track of Results`}
      </Markdown>
      We will create some NumPy arrays to help track results so we can see the effectiveness of our implementation.

      <CopyBlock
          text={`# 64 states (0 to 63) and 4 actions (0 = left, 1 = down, 2 = right, 3 = up)
q = np.zeros((env.observation_space.n, env.action_space.n)) # q-value storage
rng = np.random.default_rng() # random number from 0 to 1 (to determine if random action should be taken)
completions = np.full(episodes,False)
ep_lengths = np.zeros(episodes)
ep_epsilons = np.zeros(episodes)
checkpoints = math.floor(episodes/10) # Print statement at 10% completion intervals`}
          language="python"
          theme={dracula}
          wrapLines
        />

    <br></br>

    <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Variable</th>
          <th>Used For</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>q</td>
          <td>Used for q-value storage, this starts off as an array of 0's of size 64x4. This allows us to store a Q-Value for every combination of position and action.</td>
        </tr>
        <tr>
          <td>rng</td>
          <td>Generator used to choose a number between 0 and 1. If the random number generated is lower than our <code>epsilon</code> value, we will take a random action. Otherwise, we will take the best action as determined by our Q-Values.</td>
        </tr>
        <tr>
          <td>completions</td>
          <td>An array with an entry for each episode we run, it starts off as <code>False</code>. If we reach the goal in an episode, the value at the index for the current episode will be changed to <code>True</code>.</td>
        </tr>
        <tr>
          <td>ep_lengths</td>
          <td>An array with an entry for each episode we run, it starts off with 0 values, when an episode completes, we will update the value at the index for the current episode with the amount of actions taken that episode.</td>
        </tr>
        <tr>
          <td>ep_epsilons</td>
          <td>We will be using a decaying epsilon value (to be discussed later in the guide). This array has an entry for each episode, which will be updated with the epsilon value for each individual episode.</td>
        </tr>
        <tr>
          <td>checkpoints</td>
          <td>Just for tracking the progress of running episodes. This calculates how many episodes it takes to reach 10% of episodes completed. We will print out some basic statistics at each 10% checkpoint.</td>
        </tr>
      </tbody>
    </Table>

    <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Running Episodes and Q-Learning Implementation`}
      </Markdown>

      Let's look at the code for running each episode, choosing an action, and updating our q-values/results.


    </MathJaxContext>

    <CopyBlock
          text={`for _ in range(episodes):
  state, info = env.reset()
        
  if (_+1)%checkpoints==0:
    print("Ep", _, " , Epsi:", round(epsilon,3), " | Comp:", completions.sum(), " | Success Rate:", round(completions.sum()/_,3)*100,"%")
          

    while True:

      if rng.random() < epsilon:
        action = env.action_space.sample() # Random action
      else:
        action = np.argmax(q[state,:])

      # new_state: After taking the action calculated above, what position are we now in? (0-63)
      # reward: The reward for taking that action (reach goal = +1, reach hole/frozen = 0)
      # terminated: True if the player moves into a hole OR the player reaches the goal
      # truncation: True if the limit (length of episode) is reached, this is 200 for 8x8 env
      # info: number from 0 to 1 with odds of taking the action requested (1/3 if is_slippery, 1 otherwise)
      new_state, reward, terminated, truncated, info = env.step(action)

      if reward == 1:
        completions[_] = True

      q[state,action] = q[state,action] + learning_rate * (reward + discount_factor * max(q[new_state,:]) -q[state,action])

      state = new_state
      ep_lengths[_] += 1

      if terminated or truncated:
        break

    ep_epsilons[_] = epsilon
    epsilon -= epsilon_change # Lower Epsilon by specified amount
    if epsilon < 0:
      epsilon = 0`}
          language="python"
          theme={dracula}
          wrapLines
        />

Let's break it down step by step:
<br></br>
<br></br>
<Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Monitoring Current Progress`}
      </Markdown>

      <CopyBlock
          text={`for _ in range(episodes):
  state, info = env.reset()`}
          language="python"
          theme={dracula}
          wrapLines
        />

In the above code section we run a for loop for the specified number of episodes. We start each iteration with an environment reset. <code>env.reset()</code> will return the starting state (<code>state</code>) and debug info (<code>info</code>).
<br></br>
<br></br>
The values returned by the reset function are as follows:

<Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Variable</th>
          <th>Returned Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>state</td>
          <td><code>0</code> (this is the starting position)</td>
        </tr>
        <tr>
          <td>info</td>
          <td><code>&#123;&#39;prob: 1&#39;&#125;</code> (since you always start in position 0)</td>
        </tr>
      </tbody>
    </Table>

    <CopyBlock
          text={`if (_+1)%checkpoints==0:
  print("Ep", _, " , Epsi:", round(epsilon,3), " | Comp:", completions.sum(), " | Success Rate:", round(completions.sum()/_,3)*100,"%")`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
The <b>above code section is optional</b>. It is used to print out some of the more important statistics at every 10% checkpoint (ex. if there are 1,000 episodes: this will be printed out every 100 episodes)
<br></br>
<br></br>
Example output: <code>Episode 1999  , Epsilon: 0.78  | Completions so Far: 40  | Success Rate so Far: 2.0 %</code>
<br></br>
<br></br>
This output tells us that as of the 2,000th episode (count starts at 0), the epsilon value is 0.78, 40 of the 2,000 episodes (2.0%) have resulted in reaching the goal.
<br></br>
<br></br>
<Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Epsilon and Exploration`}
      </Markdown>


      <CopyBlock
          text={`while True:
  if rng.random() < epsilon:
    action = env.action_space.sample() # Random action
  else:
    action = np.argmax(q[state,:])`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
In the above code block, we will run this loop forever
 (there is a <code>break</code> condition later if we fall into a hole, reach the goal, or reach our action limit).  
 This is the point where we decide if we are going to choose a random action or what we
  have currently observed to be the best action. <code>rng.random()</code> returns a number between [0.0,1.0).
   Therefore, if our <code>epsilon</code> value is set to 1: we will <b>always take a random action</b>, this random
    action is chosen through the <code>env.action_space.sample()</code> function. Likewise, if our <code>epsilon</code> value is
     set to 0: we will <b>always take an action based on the optimal q-value in our given state</b>. For
      example, if we are picking an optimal Q-Value and are currently in state position 15, we
       will choose to go left, right, up, or down based on which of the 4 actions are highest in index 15
        of the state dimension in our <code>q</code> array. This is checked via <code>np.argmax(q[state,:])</code>.
<br></br>
<br></br>
<b>It is generally best to start with a very high
 epsilon (1 since we have no current data to base our choices off of)</b>. This ensures
  we initially only choose random actions. Overtime, as our <code>q</code> array gets updated, we will
   make more decisions based off of that.


<br></br>
<br></br>
   <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Epsilon (ε)</th>
          <th>Odds</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Random action is chosen 100% of the time</td>
        </tr>
        <tr>
          <td>0.75</td>
          <td>Random action is chosen 75% of the time, 25% of the time we choose what we currently believe is the optimal action given our current state</td>
        </tr>
        <tr>
          <td>0.5</td>
          <td>Random action is chosen 50% of the time, 50% of the time we choose what we currently believe is the optimal action given our current state</td>
        </tr>
        <tr>
          <td>0.25</td>
          <td>Random action is chosen 25% of the time, 75% of the time we choose what we currently believe is the optimal action given our current state</td>
        </tr>
        <tr>
          <td>0</td>
          <td>We always choose what we believe is the optimal action given our current state <b>(no exploration)</b></td>
        </tr>
      </tbody>
    </Table>

    

    It is important to have a balance when it comes to epsilon values. 
    <br></br>
    <br></br>

If it is always high, we will never actually use what we have observed before,
 which will also make it more difficult to find our goal and optimal solution,
  as it is very unlikely our agent will even be able to make it near the goal if it
   can not use the information it has gathered along the way to avoid falling into a hole or going in circles.
   <br></br>
    <br></br>

If the epsilon value is too low, we risk missing out on a potentially
 better solution, since we will always be going with what we <b>think</b> is best.
  Imagine if you are driving with a GPS that has no real-time traffic,
   outdated maps, and old speed limits. It may tell you a road is the best for getting
    to your destination, but what if a new road with less traffic and a higher speed limit has
     been built since then? If we never explore, we risk missing out on a more optimal path.

    <br></br>
    <br></br>
     <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Taking our Action`}
      </Markdown>


      <CopyBlock
        text={`new_state, reward, terminated, truncated, info = env.step(action)

if reward == 1:
  completions[_] = True`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
Using the <code>env.step()</code> function, we can pass our intended action chosen previously as an argument. Various variables are returned:
 
<br></br>
<br></br>
   <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Variable</th>
          <th>Returned Value</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>new_state</td>
          <td>an <code>int</code> value representing our new position (between 0-63 on the 8x8 environment)</td>
        </tr>
        <tr>
          <td>reward</td>
          <td>a <code>number</code> value representing the reward gained from entering our new state (in this case: 1.0 if we reach the goal space, 0.0 otherwise)</td>
        </tr>
        <tr>
          <td>terminated</td>
          <td><code>bool</code> value. <code>True</code> if we reach the goal <b>OR</b> fall into a hole, <code>False</code> otherwise</td>
        </tr>
        <tr>
          <td>truncated</td>
          <td><code>bool</code> value. <code>True</code> if the length limit of the episode (200 for 8x8) is reached, <code>False</code> otherwise</td>
        </tr>
        <tr>
          <td>info</td>
          <td>The chance of us ending up in the new state, <code>&#123;&#39;prob&#39;: 0.3333333333333333&#125;</code> if <code>is_slippery</code> is enabled, <code>&#123;&#39;prob&#39;: 1&#125;</code> otherwise.</td>
        </tr>
      </tbody>
    </Table>

    <b>It is important to remember</b>, if <code>is_slippery</code> is enabled, you will not always go in
     the direction you intended, <code>new_state</code> will return the <b>ACTUAL</b> position you have ended up in.
<br></br>
<br></br>
We will then also update our <code>completions</code> array at the index of the current episode (<code>_</code>) to <code>True</code> if we found the goal and received a reward.

<br></br>
    <br></br>
     <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Updating our Q-Value`}
      </Markdown>


      <CopyBlock
        text={`q[state,action] = q[state,action] + learning_rate * (reward + discount_factor * max(q[new_state,:]) -q[state,action])`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
The Q-Learning equation is as follows:
<br></br>
<MathJaxContext>
      <MathJax inline>{"$$Q(s, a) \\leftarrow Q(s, a) + \\alpha [r + \\gamma \\max_{a'} Q(s', a') - Q(s, a)]$$"}</MathJax> 
    </MathJaxContext>
Where

<Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <thead>
        <tr>
          <th>Variable</th>
          <th>Represents</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td><MathJaxContext><MathJax inline>{"$$Q(s,a)$$"}</MathJax></MathJaxContext></td>
          <td>the Q-Value for state <code>s</code> and action <code>a</code></td>
        </tr>
        <tr>
          <td><MathJaxContext><MathJax inline>{"$$\\alpha$$"}</MathJax></MathJaxContext></td>
          <td>the learning rate (alpha)</td>
        </tr>
        <tr>
          <td><MathJaxContext><MathJax inline>{"$$r$$"}</MathJax></MathJaxContext></td>
          <td>the immediate reward</td>
        </tr>
        <tr>
          <td><MathJaxContext><MathJax inline>{"$$\\gamma$$"}</MathJax></MathJaxContext></td>
          <td>the discount factor (gamma)</td>
        </tr>
        <tr>
          <td><MathJaxContext><MathJax inline>{"$$\\max_{a'} Q(s', a')$$"}</MathJax></MathJaxContext></td>
          <td>the maximum Q-value of our next state</td>
        </tr>
        
      </tbody>
    </Table>

    Going into further detail:
    <br></br>
    <br></br>
     <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### Alpha (Learning Rate)`}
      </Markdown>


      Alpha is our learning rate (and is usually a number between 0 and 1). A
       larger alpha means that the Q-Value is updated quicker based on the new information we found.
        This means faster learning but at the cost of potentially being
         unstable (value changing too quickly and the Q-Value becoming too high/low based on
          a single update). Lower values are more stable but will
       cause new information to impact the Q-Value less potentially leading us to need more episodes.
       <br></br>
       <br></br>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### Gamma (Discount Factor)`}
      </Markdown>

      Gamma is our discount factor. Gamma ranges from 0 
      to 1. When Gamma is 0, it means that the agent will
       only consider immediate rewards, meaning if it had the choice of getting 20
        dollars today or 10 dollars today plus an additional 10,000 dollars tomorrow,
         it would choose to get 20 dollars today. It is <b>important to note that in Frozen
          Lake, you only receive a reward for reaching the end goal, so having a low gamma 
          would be particularly problematic since you won't receive one the vast majority of the
           time</b>. If gamma is set to 1, future rewards will be valued just as much as the
            immediate reward received. Any values between 0 to 1 will be a balance,
       with lower values lowering the importance of future rewards.
       <br></br>
       <br></br>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### Q-Values`}
      </Markdown>

      We store Q-Values to determine what we have currently observed
       to be the best action to take given the current state (which is influenced
        by the values chosen for Gamma, Alpha, Epsilon, etc.). They are stored in the following
         format (for simplicity imagine there
       are <b>4 possible positions and 2 possible actions (left, right).</b>
       <br></br>
       <br></br>

       <CopyBlock 
          text={`[[0.5 0.21]
 [0.95 0.01]
 [0.51 0.49]
 [0.02 0.83]]`}
          language="python"
          theme={dracula}
          wrapLines
        />


               <br></br>


In this example, if we are in state 1, we can see
 that we have found that going left is significantly better
  (0.95 vs 0.01) than going right. However, if we are in state 2, left is only
   slightly better (0.51 vs 0.49) than going right. We would still go left
    in all cases when deciding by Q-Values since we are only basing our decision off of
     the action with the <b>max</b> Q-Value. In this case, having a balanced epsilon value would
      help us still test the other possible actions (going right) to see if better results occur
       that way, instead of always going left.


       <br></br>
       <br></br>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Finishing the Episode`}
      </Markdown>

      <CopyBlock
          text={`            state = new_state
            ep_lengths[_] += 1

            if terminated or truncated:
                break`}
          language="python"
          theme={dracula}
          wrapLines
        />

Now that we have finished updating our Q-Value, we start by updating our <code>state</code> variable to the <code>new_state</code> value. Since an action was taken, we will update our tracking metric for episode lengths (<code>ep_lengths</code>) by increasing the value for this episode's (<code>_</code>) entry by 1.

Finally, if this episode results in us reaching one of the 3 end conditions (goal reached, fell in hole, or episode length limit), we will break out of this loop and start a new episode. Otherwise, we will now go back to the start of the <code>while True:</code> loop.









<br></br>
<br></br>
      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Finishing All Episodes and Closing the Environment`}
      </Markdown>

      <CopyBlock
          text={`   time.sleep(0.5)
    env.close()
    
    print("\\nSimple Breakdown:")
    print("Episodes:", episodes)
    print("Successful Episodes:", completions.sum())
    print("Failed Episodes:", (episodes-completions.sum()))
    print("Success Rate:", round(((completions.sum())/(episodes))*100,3), "%")
    print("Success Episode Array:", np.convolve(completions, np.ones(100), 'valid'))

    # np.convolve will compute the rolling sum for 100 episodes`}
          language="python"
          theme={dracula}
          wrapLines
        />
        <br></br>

After finishing the last episode, I have added a short pause (this is mostly for if we are visualizing the environment so the final result can be seen for longer. Afterwards, the environment is closed using <code>env.close()</code>, this will also close any rendering windows. Afterwards, some useful statistics are printed in the following format:
<br></br>
<br></br>
<CopyBlock
          text={`Simple Breakdown:
Episodes: 20000
Successful Episodes: 9870
Failed Episodes: 10130
Success Rate: 49.35 %
Success Episode Array: [  0.   0.   0. ... 100. 100. 100.]`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
The number of episodes, successful episodes, failed episodes, success rate, and success episode array (the number of successful episodes out of the last 100 episodes (between 0 to 100)).
<br></br>
<br></br>



      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Visualizing the Results`}
      </Markdown>

      <CopyBlock
          text={`    fig, axs = plt.subplots(1, 2, figsize=(20, 8))

    axs[0].plot(np.convolve(completions, np.ones(100), 'valid'))
    axs[0].set_title("Successful Episodes")
    axs[0].set_xlabel("Episode")
    axs[0].set_ylabel("# of Successful Episodes Out of Past 100")

    ax2 = axs[0].twinx()
    ax2.plot(np.convolve(ep_epsilons, np.ones(100), 'valid') / 100, color='red')
    ax2.set_ylabel("Epsilon (Rolling 100 Episode Mean)")
    ax2.set_ylim([0, 1])
    

    axs[1].plot(np.convolve(ep_lengths, np.ones(100), 'valid') / 100)
    axs[1].set_title("Episode Lengths")
    axs[1].set_xlabel("Episode")
    axs[1].set_ylabel("Length")
`}
          language="python"
          theme={dracula}
          wrapLines
        />

        <br></br>
        This code will print out two plots side by side. The first plot takes the rolling sum of the last 100 completion values, for example if 40/100 of the last completion values are <code>True</code>, the plotted y-axis value at that episode will be <code>40</code>. On a second y-axis, we will also plot the Epsilon value, calculated as the rolling mean of the last 100 episodes. For example, if 100 episodes ago 
ϵ
=
0.5
 and it went down at a linear rate to 
ϵ
=
0.4
, the plotted value at that episode would be 
ϵ
=
0.45
. Epsilon values can only be between 0 and 1 (
0
≤
ϵ
≤
1
), so the y-limit on the graph has been set as such.

The second plot will show the rolling average (last 100 episodes) episode length. This is the amount of actions taken before reaching one of the end conditions (falling into a hole, reaching the goal, episode length limit).

Here is a sample image of what these two plots may look like:
<br></br>
<br></br>
<img src={plotspng} alt="" />
<br></br>
This is the final part of the run_episodes function. Let's look into some results now.
<br></br>








      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Results and Observations`}
      </Markdown>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Number of Episodes`}
      </Markdown>







      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### Low Episode Count`}
      </Markdown>
      It is important to include enough episodes when training an agent using Reinforcement Learning. Using around 1,000 episodes for example means that it is very likely the agent will not have enough time to find a path to the goal, let alone an optimal path.
      <br></br>
      <br></br>
      <CopyBlock
          text={`run_episodes(1110, epsilon_change=0.001, slippery=False, learning_rate=0.001)
`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<CopyBlock
          text={`Ran using the following settings:
Episodes: 1110
Learning Rate: 0.001
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 0.001
Slippery: False

Ep 110  , Epsi: 0.89  | Comp: 0  | Success Rate: 0.0 %
Ep 221  , Epsi: 0.779  | Comp: 0  | Success Rate: 0.0 %
Ep 332  , Epsi: 0.668  | Comp: 0  | Success Rate: 0.0 %
Ep 443  , Epsi: 0.557  | Comp: 0  | Success Rate: 0.0 %
Ep 554  , Epsi: 0.446  | Comp: 0  | Success Rate: 0.0 %
Ep 665  , Epsi: 0.335  | Comp: 0  | Success Rate: 0.0 %
Ep 776  , Epsi: 0.224  | Comp: 0  | Success Rate: 0.0 %
Ep 887  , Epsi: 0.113  | Comp: 0  | Success Rate: 0.0 %
Ep 998  , Epsi: 0.002  | Comp: 0  | Success Rate: 0.0 %
Ep 1109  , Epsi: 0  | Comp: 0  | Success Rate: 0.0 %

Simple Breakdown:
Episodes: 1110
Successful Episodes: 0
Failed Episodes: 1110
Success Rate: 0.0 %
Success Episode Array: [0. 0. 0. ... 0. 0. 0.]
`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<img src={toofewepisodes} alt="" />
<br></br>
<br></br>
In this case, not only did our algorithm never find the goal. As it started to rely on Q-Values more as opposed to taking random actions, the episode length sky rocketed from ~30 to ~100.
<br></br>
<br></br>
This appears to be an issue at up to around ~10,000 episodes as well.
<br></br>
<br></br>







      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### High Episode Count`}
      </Markdown>

      <CopyBlock
          text={`run_episodes(50000, epsilon_change=0.000022, slippery=False, learning_rate=0.001)
`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<CopyBlock
          text={`Ran using the following settings:
Episodes: 50000
Learning Rate: 0.001
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 2.2e-05
Slippery: False

Ep 4999  , Epsi: 0.89  | Comp: 35  | Success Rate: 0.7000000000000001 %
Ep 9999  , Epsi: 0.78  | Comp: 191  | Success Rate: 1.9 %
Ep 14999  , Epsi: 0.67  | Comp: 806  | Success Rate: 5.4 %
Ep 19999  , Epsi: 0.56  | Comp: 2061  | Success Rate: 10.299999999999999 %
Ep 24999  , Epsi: 0.45  | Comp: 4119  | Success Rate: 16.5 %
Ep 29999  , Epsi: 0.34  | Comp: 6965  | Success Rate: 23.200000000000003 %
Ep 34999  , Epsi: 0.23  | Comp: 10579  | Success Rate: 30.2 %
Ep 39999  , Epsi: 0.12  | Comp: 14756  | Success Rate: 36.9 %
Ep 44999  , Epsi: 0.01  | Comp: 19511  | Success Rate: 43.4 %
Ep 49999  , Epsi: 0  | Comp: 24510  | Success Rate: 49.0 %

Simple Breakdown:
Episodes: 50000
Successful Episodes: 24511
Failed Episodes: 25489
Success Rate: 49.022 %
Success Episode Array: [  0.   0.   0. ... 100. 100. 100.]
`}
          language="python"
          theme={dracula}
          wrapLines
        />
        <br></br>
        <img src={toomanyepisodes} alt="" />
        <br></br>
        <br></br>

        On the other hand, running 50,000 episodes gets us to an eventual 100% successful episode rate and low episode length (~15). The success rate and episode length converges to these values when 
ϵ
 approaches 0. We have already likely found our optimal path earlier, however, we are still trying the occasional random action when 
ϵ
≠
0
 which will cause some failed episodes and un-optimal episode length. While this gets us a good result and is not really an issue with a simple problem like this, running unnecessary episodes for a more complex problem can add a lot of runtime which is unideal.
 <br></br>
 <br></br>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`#### Ideal Episode Count`}
      </Markdown>

      <CopyBlock
          text={`run_episodes(20000, epsilon_change=0.000053, slippery=False, learning_rate=0.001)
`}
          language="python"
          theme={dracula}
          wrapLines
        />

        <br></br>

<CopyBlock
          text={`Ran using the following settings:
Episodes: 20000
Learning Rate: 0.001
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 5.3e-05
Slippery: False

Episode 1999  , Epsilon: 0.894  | Completions so Far: 16  | Success Rate so Far: 0.8 %
Episode 3999  , Epsilon: 0.788  | Completions so Far: 91  | Success Rate so Far: 2.3 %
Episode 5999  , Epsilon: 0.682  | Completions so Far: 310  | Success Rate so Far: 5.2 %
Episode 7999  , Epsilon: 0.576  | Completions so Far: 809  | Success Rate so Far: 10.100000000000001 %
Episode 9999  , Epsilon: 0.47  | Completions so Far: 1587  | Success Rate so Far: 15.9 %
Episode 11999  , Epsilon: 0.364  | Completions so Far: 2681  | Success Rate so Far: 22.3 %
Episode 13999  , Epsilon: 0.258  | Completions so Far: 4030  | Success Rate so Far: 28.799999999999997 %
Episode 15999  , Epsilon: 0.152  | Completions so Far: 5655  | Success Rate so Far: 35.3 %
Episode 17999  , Epsilon: 0.046  | Completions so Far: 7478  | Success Rate so Far: 41.5 %
Episode 19999  , Epsilon: 0  | Completions so Far: 9461  | Success Rate so Far: 47.3 %

Simple Breakdown:
Episodes: 20000
Successful Episodes: 9462
Failed Episodes: 10538
Success Rate: 47.31 %
Success Episode Array: [  1.   1.   1. ... 100. 100. 100.]
`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>

<img src={idealepisodes} alt="" />
<br></br>
Running 20,000 episodes produces very similar results to running 50,000 episodes but would take less than half the runtime. Our episode length and successful episode rate still converges to their optimal values. When using reinforcement learning, it is important not to waste valuable resources on a problem that does not require it.
<br></br>
<br></br>
Our optimal path as per Q-Values after 20,000 episodes:
<br></br>
<img src={optimalpath1} alt="" />
<br></br>
<br></br>
<CopyBlock
          text={`Optimal Result (as determined by Q-Values):
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 1
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 2
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 3
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 4
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 5
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 6
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 7
Action taken: Right  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 8
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 9
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 10
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 11
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 12
Action taken: Down  | Reward Given: 0.0  | Terminated: False  | Actions Taken: 13
Action taken: Down  | Reward Given: 1.0  | Terminated: True  | Actions Taken: 14
`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
Here is a <b>sample</b> of the Q-Values at important states. We can see that at position 0, going down is the optimal action, then for positions 8-14, going right becomes optimal, afterwards going down is once again the best choice for every position on the furthest right column.

<CopyBlock
          text={`Q-Values:
[[2.81814580e-03 1.30679639e-01 1.15779036e-03 2.36418326e-03] # Position 0 (Go down: Action 1 is the highest value as it has the lowest negative exponent)
 ...
 [5.42548374e-03 3.90323047e-04 1.85252191e-01 2.71799400e-03] # Position 8 (Go right: Action 2 is the highest value)
 [4.84914345e-03 7.49614166e-04 2.52615681e-01 1.01329330e-03]
 [8.90645199e-03 1.13049327e-03 3.31621752e-01 1.31121474e-03]
 [1.49107212e-02 0.00000000e+00 4.19091755e-01 2.25907655e-03]
 [2.16877219e-02 2.10303261e-03 5.09595695e-01 3.09215203e-03]
 [3.27082481e-02 1.44863911e-02 5.96644768e-01 1.13061410e-03]
 [3.88835775e-02 5.62562330e-02 6.74822453e-01 7.79188744e-03]
 [8.31040013e-02 7.41649804e-01 1.33525439e-01 3.56161971e-02] # Position 15 (Go down: Action 1 is the highest value)
 ...
 [8.79612855e-02 7.99088605e-01 2.09761914e-01 1.51091071e-01] # Position 23 (Go down)
 ...
 [7.03089846e-02 8.50943883e-01 2.65382341e-01 2.05714098e-01] # Position 31 (Go down)
 ...
 [0.00000000e+00 0.00000000e+00 0.00000000e+00 0.00000000e+00]] # Position 63 (Goal position, no Q-Value calculations occurred here since no further actions are needed)
`}
          language="python"
          theme={dracula}
          wrapLines
        />

        <br></br>
        We can see that the best result is in 14 actions and it is to go down 1 space, then to the furthest right position, then down to the bottom. Of course this is with <code>is_slippery</code> mode set to <code>False</code>, we will see that with it on later, the results will change significantly.
        <br></br>
        <br></br>



      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Learning Rate`}
      </Markdown>

      Let's try to find an ideal learning rate, all other variables will be consistent across these tests:
      <br></br>
      <br></br>
      <CopyBlock
          text={`Episodes: 50000
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 2.3e-05
Slippery: False
`}
          language="python"
          theme={dracula}
          wrapLines
        />
        <br></br>
        We will compare the success rate (cumulative to the current episode) at every 5,000 episodes.
        <br></br>
        <br></br>

        <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
      <tbody>
        <tr>
          <td><b><i>BOLD ITALICS</i></b></td>
          <td>Best Result at Episode #</td>
        </tr>
        <tr>
          <td><b>BOLD</b></td>
          <td>Second Best Result at Episode #</td>
        </tr>
      </tbody>
    </Table>


    <Table striped bordered hover variant="dark" style={{ width: 'auto' }}>
  <thead>
    <tr>
      <th>Learning Rate</th>
      <th>Ep 5,000</th>
      <th>Ep 10,000</th>
      <th>Ep 15,000</th>
      <th>Ep 20,000</th>
      <th>Ep 25,000</th>
      <th>Ep 30,000</th>
      <th>Ep 35,000</th>
      <th>Ep 40,000</th>
      <th>Ep 45,000</th>
      <th>Ep 50,000</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>0.95</td>
      <td>0.5%</td>
      <td>0.6%</td>
      <td>1.2%</td>
      <td>2.3%</td>
      <td>4.5%</td>
      <td>8.5%</td>
      <td>13.9%</td>
      <td>20.8%</td>
      <td>28.9%</td>
      <td>36.0%</td>
    </tr>
    <tr>
      <td>0.8</td>
      <td>0.6%</td>
      <td>0.6%</td>
      <td>1.3%</td>
      <td>2.5%</td>
      <td>4.6%</td>
      <td>8.3%</td>
      <td>13.5%</td>
      <td>20.4%</td>
      <td>28.6%</td>
      <td>35.7%</td>
    </tr>
    <tr>
      <td>0.5</td>
      <td>0.7%</td>
      <td>0.9%</td>
      <td>1.5%</td>
      <td>2.6%</td>
      <td>4.8%</td>
      <td>8.6%</td>
      <td>13.7%</td>
      <td>20.6%</td>
      <td>28.7%</td>
      <td>35.8%</td>
    </tr>
    <tr>
      <td>0.2</td>
      <td>0.6%</td>
      <td>1.9%</td>
      <td>2.1%</td>
      <td>3.2%</td>
      <td>5.6%</td>
      <td>9.8%</td>
      <td>15.3%</td>
      <td>22.4%</td>
      <td>30.4%</td>
      <td>37.4%</td>
    </tr>
    <tr>
      <td>0.05</td>
      <td><i><b>0.9%</b></i></td>
      <td><i><b>2.5%</b></i></td>
      <td><i>5.5%</i></td>
      <td><i>7.6%</i></td>
      <td><i>9.6%</i></td>
      <td><i>13.5%</i></td>
      <td><i>18.7%</i></td>
      <td><i>25.4%</i></td>
      <td><i>33.1%</i></td>
      <td><i>39.8%</i></td>
    </tr>
    <tr>
      <td>0.02</td>
      <td><strong>0.8%</strong></td>
      <td>2.4%</td>
      <td>6.0%</td>
      <td>11.6%</td>
      <td>17.3%</td>
      <td>22.2%</td>
      <td>28.0%</td>
      <td>34.4%</td>
      <td>41.3%</td>
      <td>47.2%</td>
    </tr> 
    <tr>
      <td>0.01</td>
      <td>0.6%</td>
      <td>2.3%</td>
      <td><i><b>6.3%</b></i></td>
      <td><i><b>11.9%</b></i></td>
      <td><i><b>18.7%</b></i></td>
      <td><strong>25.6%</strong></td>
      <td><strong>32.8%</strong></td>
      <td><strong>39.7%</strong></td>
      <td><strong>46.1%</strong></td>
      <td><strong>51.5%</strong></td>
    </tr>
    <tr>
      <td>0.001</td>
      <td>0.6%</td>
      <td><i><b>2.5%</b></i></td>
      <td><strong>6.1%</strong></td>
      <td><strong>11.8%</strong></td>
      <td><strong>18.5%</strong></td>
      <td><i><b>25.7%</b></i></td>
      <td><i><b>32.9%</b></i></td>
      <td><i><b>39.8%</b></i></td>
      <td><i><b>46.2%</b></i></td>
      <td><i><b>51.6%</b></i></td>
    </tr>
    <tr>
      <td>0.0001</td>
      <td>0.6%</td>
      <td>1.9%</td>
      <td>4.6%</td>
      <td>9.4%</td>
      <td>15.9%</td>
      <td>23.4%</td>
      <td>30.8%</td>
      <td>38.0%</td>
      <td>44.6%</td>
      <td>50.2%</td>
    </tr>
    <tr>
      <td>0.00001</td>
      <td>0.5%</td>
      <td>1.9%</td>
      <td>5.0%</td>
      <td>9.9%</td>
      <td>15.6%</td>
      <td>22.2%</td>
      <td>29.5%</td>
      <td>36.7%</td>
      <td>43.5%</td>
      <td>49.1%</td>
    </tr>
  </tbody>
</Table>

<br></br>
We can see that overall, the best results with a non-changing learning rate is to have 
alpha between 0.001 and 0.01 exclusive
 (a low learning rate). This means that we update our Q-Values slowly as opposed to rapidly. However, the results tend to show that a higher learning rate is optimal earlier in the episode count (because we have not run a lot of episodes yet, it is very important to quickly learn a good path at this point). While I did not implement it in this code, this shows that it would likely be beneficial to add a decay to the learning rate, similar to epsilon, or have a cut-off point where it drops suddenly after a certain episode count.

<br></br>
<br></br>

      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Discount Factor`}
      </Markdown>

      Discount Factor was tested as well, however, due to there being no rewards (positive or negative) other than the end goal, it had a smaller impact on the results. It is important to use a higher discount factor though, considering most actions have no immediate reward, a discount factor near or at 0 would have devestating results. The observations done can be found in this <a href='https://github.com/adadamc/ML2_RL_Exam/blob/main/VariableComparison.xlsx' target='_blank'>Excel document</a>, but it is very clear that in this particular environment a higher discount factor is optimal.
      <br></br>
      <br></br>






      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Slippery Mode Enabled`}
      </Markdown>

      When is_slippery is set to True, the problem becomes a lot more complex. In this case the action we intend to take only occurs 1/3 of the time, we have an equal chance of going in either perpendicular direction. It is no longer realistic to eventually converge to a 100% success rate. The Q-Learning algorithm will now have to find a path that fully ignores finding the normal shortest path, but instead finds one where slipping in a perpendicular direction does not result in falling into a hole as often. Previously, we found that 20,000 episodes was a good number to reliably reach 100% success by the end, this is no longer the case.
      <br></br>
      <br></br>
      <CopyBlock
          text={`run_episodes(40000, epsilon_change=0.000026, slippery=True, learning_rate=0.005)
`}
          language="python"
          theme={dracula}
          wrapLines
        />
        <br></br>
         <CopyBlock
          text={`Ran using the following settings:
Episodes: 40000
Learning Rate: 0.005
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 2.6e-05
Slippery: True

Episode 3999  , Epsilon: 0.896  | Completions so Far: 7  | Success Rate so Far: 0.2 %
Episode 7999  , Epsilon: 0.792  | Completions so Far: 17  | Success Rate so Far: 0.2 %
Episode 11999  , Epsilon: 0.688  | Completions so Far: 33  | Success Rate so Far: 0.3 %
Episode 15999  , Epsilon: 0.584  | Completions so Far: 72  | Success Rate so Far: 0.5 %
Episode 19999  , Epsilon: 0.48  | Completions so Far: 119  | Success Rate so Far: 0.6 %
Episode 23999  , Epsilon: 0.376  | Completions so Far: 183  | Success Rate so Far: 0.8 %
Episode 27999  , Epsilon: 0.272  | Completions so Far: 248  | Success Rate so Far: 0.8999999999999999 %
Episode 31999  , Epsilon: 0.168  | Completions so Far: 371  | Success Rate so Far: 1.2 %
Episode 35999  , Epsilon: 0.064  | Completions so Far: 494  | Success Rate so Far: 1.4000000000000001 %
Episode 39999  , Epsilon: 0  | Completions so Far: 669  | Success Rate so Far: 1.7000000000000002 %

Simple Breakdown:
Episodes: 40000
Successful Episodes: 669
Failed Episodes: 39331
Success Rate: 1.672 %
`}
          language="python"
          theme={dracula}
          wrapLines
        />
<br></br>
<img src={slip40k} alt="" />
<br></br>
<br></br>
We can see that even with double the number of episodes, we are now reaching a success rate of around 5-10% by the time 
ϵ
=
0
. While not particularly of note yet, as we have nothing to compare it to with <code>is_slippery</code> enabled yet, note that the episode length is around 25 when epsilon reaches 0 (higher than the previous 14).

Increasing the episode count to 85,000 results in a far better result:

<CopyBlock
          text={`run_episodes(85000, epsilon_change=0.000013, slippery=True, learning_rate=0.005)
`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>
<CopyBlock
          text={`Ran using the following settings:
Episodes: 85000
Learning Rate: 0.005
Discount Factor: 0.95
Initial Epsilon: 1
Epsilon Decay (per episode): 1.3e-05
Slippery: True

Episode 8499  , Epsilon: 0.89  | Completions so Far: 24  | Success Rate so Far: 0.3 %
Episode 16999  , Epsilon: 0.779  | Completions so Far: 60  | Success Rate so Far: 0.4 %
Episode 25499  , Epsilon: 0.669  | Completions so Far: 118  | Success Rate so Far: 0.5 %
Episode 33999  , Epsilon: 0.558  | Completions so Far: 234  | Success Rate so Far: 0.7000000000000001 %
Episode 42499  , Epsilon: 0.448  | Completions so Far: 416  | Success Rate so Far: 1.0 %
Episode 50999  , Epsilon: 0.337  | Completions so Far: 1038  | Success Rate so Far: 2.0 %
Episode 59499  , Epsilon: 0.227  | Completions so Far: 2264  | Success Rate so Far: 3.8 %
Episode 67999  , Epsilon: 0.116  | Completions so Far: 4398  | Success Rate so Far: 6.5 %
Episode 76499  , Epsilon: 0.006  | Completions so Far: 8102  | Success Rate so Far: 10.6 %
Episode 84999  , Epsilon: 0  | Completions so Far: 12698  | Success Rate so Far: 14.899999999999999 %

Simple Breakdown:
Episodes: 85000
Successful Episodes: 12699
Failed Episodes: 72301
Success Rate: 14.94 %)
`}
          language="python"
          theme={dracula}
          wrapLines
        />

<img src={slip85k} alt="" />
<br></br>

By the time 
ϵ
=
0
, we can see that our success rate has improved to around 55-60%. Episode length has also increased to around 60, we are reaching the goal more often, but taking a lot longer to get there.
<br></br>
<br></br>
Increasing the episode count past this point makes the results a bit more stable, but overall does not impact them significantly (looking at only the results once 
ϵ
=
0
). Here is 450,000 episodes:
<br></br>
<img src={slip450k} alt="" />
<br></br>

Overall, it seems the best case scenario is around a 65-70% successful episode rate, with episode length varying between 60-70 on average (due to the odds of slipping, some episodes will be a lot shorter due to falling in a hole).

Let's look at a few episodes based on the optimal Q-Values gathered from running 85,000 episodes:
<br></br>
<img src={slipvisual} alt="" />
<br></br>
We can see that the agent is moving in a "weird" path since it is often being moved in an unintended direction. It is possible that it could fall into a hole unintentionally (especially near the end as there is a 50/50 chance in the last two moves that it either goes down (optimal) or to the left (into a hole)). The goal of the agent is to find a path that reaches the goal as often as possible factoring in these unintended actions.




      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Potential Improvements`}
      </Markdown>




      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`### Negative Rewards`}
      </Markdown>

      Negative rewards could be used to change the results. If we wanted to incentivize a shorter path length (even if it is riskier) in is_slippery mode, we could add a small negative reward (ex. -0.01) to each frozen block to incentivize reaching an end state quicker. It would also potentially be valuable to add a larger negative reward for falling into a hole. If a negative reward is given for each frozen block reached, the agent may wish to end the game by falling into a hole to stop the negative rewards quickly, so it would be valuable to disincentivize falling into a hole to a certain extent as well.




      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Conclusion`}
      </Markdown>

      Q-Learning is valuable for finding an optimal path that maximizes rewards. Based on the environment, it may be valuable to collect smaller rewards on the way or immediately go for the "grand prize". Adding in the chance for unintended consequences also changes the play style of the agent, by lowering the importance of finding a shortest path, and instead encouraging the agent to find the safest path given potential unintended consequences. Various variables can be adjusted (
ϵ
,$\alpha$,$\gamma$, etc.) to try and train an agent more optimally given the layout of the environment. When epsilon reaches 0 (assuming a decaying epsilon), the agent is no longer learning, and is instead using the knowledge it has learned along the way to make what it believes to be the best decision.




      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Full Code`}
      </Markdown>


      <CopyBlock 
          text={`
# Runs the requested amount of episodes using Q-Learning
# episodes = # of episodes to run through
# learning_rate = 0 to 1, closer to 1 results in newer episodes taking higher priority in the q-values, changing the values faster
# discount_factor = 0 to 1, closer to 1 values future rewards highly, closer to 0 focuses on immediate rewards more
# epsilon = 0 to 1, chance of taking a random action (1 is always random, 0 is always optimal action as per q values)
# epsilon_change = how much to lower epsilon by per episode (over time max q-value should take priority and exploration minimized)
# slippery = If False, the action requested is always followed through on. If True, the action requested is followed through on 1/3 of the time,
#            and the two perpendicular actions are taken 1/3 of the time each (ex. request=left (1/3 chance), 1/3 chance of slipping up, 1/3 of down)
# render = None for no visualization, "Human" to see visualization

def run_episodes(episodes, learning_rate=0.05, discount_factor=0.95, epsilon=1, epsilon_change=0.01, slippery=True, render=None):
    env = gym.make("FrozenLake-v1", map_name="8x8", is_slippery=slippery, render_mode=render)

    # 64 states (0 to 63) and 4 actions (0 = left, 1 = down, 2 = right, 3 = up)
    q = np.zeros((env.observation_space.n, env.action_space.n)) # q-value storage
    rng = np.random.default_rng() # random number from 0 to 1 (to determine if random action should be taken)
    completions = np.full(episodes,False)
    ep_lengths = np.zeros(episodes)
    ep_epsilons = np.zeros(episodes)
    checkpoints = math.floor(episodes/10) # Print statement at 10% completion intervals

    print("Ran using the following settings:")
    print("Episodes:", episodes)
    print("Learning Rate:", learning_rate)
    print("Discount Factor:", discount_factor)
    print("Initial Epsilon:", epsilon)
    print("Epsilon Decay (per episode):", epsilon_change)
    print("Slippery:", slippery)
    print("")

    for _ in range(episodes):
        state, info = env.reset()
        
        if (_+1)%checkpoints==0:
            print("Ep", _, " , Epsi:", round(epsilon,3), " | Comp:", completions.sum(), " | Success Rate:", round(completions.sum()/_,3)*100,"%")
          

        while True:

            if rng.random() < epsilon:
                action = env.action_space.sample() # Random action
            else:
                action = np.argmax(q[state,:])

            # new_state: After taking the action calculated above, what position are we now in? (0-63)
            # reward: The reward for taking that action (reach goal = +1, reach hole/frozen = 0)
            # terminated: True if the player moves into a hole OR the player reaches the goal
            # truncation: True if the limit (length of episode) is reached, this is 200 for 8x8 env
            # info: number from 0 to 1 with odds of taking the action requested (1/3 if is_slippery, 1 otherwise)
            new_state, reward, terminated, truncated, info = env.step(action)

            if reward == 1:
                completions[_] = True

            q[state,action] = q[state,action] + learning_rate * (reward + discount_factor * max(q[new_state,:]) -q[state,action])

            state = new_state
            ep_lengths[_] += 1

            if terminated or truncated:
                break

        ep_epsilons[_] = epsilon
        epsilon -= epsilon_change # Lower Epsilon by specified amount
        if epsilon < 0:
            epsilon = 0

    time.sleep(0.5)
    env.close()
    
    print("\\nSimple Breakdown:")
    print("Episodes:", episodes)
    print("Successful Episodes:", completions.sum())
    print("Failed Episodes:", (episodes-completions.sum()))
    print("Success Rate:", round(((completions.sum())/(episodes))*100,3), "%")
    print("Success Episode Array:", np.convolve(completions, np.ones(100), 'valid'))

    # np.convolve will compute the rolling mean for 100 episodes

    fig, axs = plt.subplots(1, 2, figsize=(20, 8))

    axs[0].plot(np.convolve(completions, np.ones(100), 'valid'))
    axs[0].set_title("Successful Episodes")
    axs[0].set_xlabel("Episode")
    axs[0].set_ylabel("# of Successful Episodes Out of Past 100")

    ax2 = axs[0].twinx()
    ax2.plot(np.convolve(ep_epsilons, np.ones(100), 'valid') / 100, color='red')
    ax2.set_ylabel("Epsilon (Rolling 100 Episode Mean)")
    ax2.set_ylim([0, 1])
    

    axs[1].plot(np.convolve(ep_lengths, np.ones(100), 'valid') / 100)
    axs[1].set_title("Episode Lengths")
    axs[1].set_xlabel("Episode")
    axs[1].set_ylabel("Length")
`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>




      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Additional Function to Visualize the Optimal Path Based on Q-Values`}
      </Markdown>

      This function can be ran at the end of run_episodes and is used to visualize the agent moving based on the final q values found.

      <CopyBlock
          text={`def visualize_best_result(episodes, q, slippery=True):
    print("")
    print("Optimal Result (as determined by Q-Values:")

    action_array = ["Left", "Down", "Right", "Up"]
    actions_taken = 0
    
    env = gym.make("FrozenLake-v1", map_name="8x8", is_slippery=slippery, render_mode="human")

    for _ in range(episodes):
        state, info = env.reset()

        while True:
            action = np.argmax(q[state,:])

            new_state, reward, terminated, truncated, info = env.step(action)

            state = new_state

            if _ == 0:
                actions_taken += 1
                print("Action taken:", action_array[action], " | Reward Given:", reward, " | Terminated:", terminated, " | Actions Taken:", actions_taken)

            if terminated or truncated:
                break

    env.close()`}
          language="python"
          theme={dracula}
          wrapLines
        />

<br></br>

      <Markdown 
        remarkPlugins={[remarkMath]} 
        components={{
          math: ({ value }) => <MathJax inline>{value}</MathJax>,
          inlineMath: ({ value }) => <MathJax inline>{value}</MathJax> 
        }}
      >
        {`## Resources Used`}
      </Markdown>


  <ul>
    <li>AI (CSCI 4610U) Lectures: Winter 2024 Semester</li>
    <li>ML2 (CSCI 4052U) Lectures: Fall 2024 Semester</li>
    <li><a href="https://www.youtube.com/watch?v=ZhoIgo3qqLU" target="_blank">https://www.youtube.com/watch?v=ZhoIgo3qqLU</a> - FrozenLake Gymnasium</li>
    <li><a href="https://gymnasium.farama.org/environments/toy_text/frozen_lake/" target="_blank">https://gymnasium.farama.org/environments/toy_text/frozen_lake/</a></li>
  </ul>








  </div>

  )
}

export default FrozenLake;